import React, { useState, useEffect } from 'react';
import { lawApi } from '../../lawApi';
import { useTheme } from '../../components/LawPortalLayout';
import { Plus, Trash2, BookOpen, ChevronDown, ChevronUp, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ESSAY_TYPES = [
  { value: 'personal_statement', label: 'Personal Statement' },
  { value: 'diversity', label: 'Diversity Statement' },
  { value: 'why_x', label: 'Why X School' },
  { value: 'addendum', label: 'Addendum' },
  { value: 'optional', label: 'Optional Essay' },
  { value: 'school_specific', label: 'School-Specific' },
];

const DRAFT_STATUSES = ['not_started', 'drafting', 'advisor_review', 'revision', 'final', 'submitted'];
const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-gray-400' },
  drafting: { label: 'Drafting', color: 'bg-amber-400' },
  advisor_review: { label: 'Advisor Review', color: 'bg-purple-500' },
  revision: { label: 'Revision', color: 'bg-blue-500' },
  final: { label: 'Final', color: 'bg-emerald-500' },
  submitted: { label: 'Submitted', color: 'bg-emerald-700' },
};

export default function LawEssays() {
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', essay_type: 'personal_statement', school_name: '', prompt: '', word_limit: '' });
  const theme = useTheme();
  const { dark, textPrimary, textSecondary, textMuted, borderColor, hoverBg } = theme;
  const cardBg = dark ? 'bg-[#141618]' : 'bg-white';
  const inputClass = `w-full h-8 px-2.5 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white text-[#1A1A1A]'} focus:outline-none`;
  const editorClass = `w-full px-3 py-2.5 text-sm leading-relaxed rounded border ${borderColor} ${dark ? 'bg-[#0D0F11] text-[#E8E6E1]' : 'bg-[#FAFAF8] text-[#1A1A1A]'} focus:outline-none focus:ring-1 focus:ring-[#C5A55A]/30 font-serif`;

  const load = () => lawApi.getEssays().then(setEssays).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await lawApi.createEssay({ ...form, word_limit: form.word_limit ? parseInt(form.word_limit) : null });
      setForm({ title: '', essay_type: 'personal_statement', school_name: '', prompt: '', word_limit: '' });
      setAdding(false);
      load();
      toast.success('Essay created');
    } catch (e) { toast.error(e.message); }
  };

  const handleSave = async (essay) => {
    setSaving(true);
    try {
      await lawApi.updateEssay(essay.id, { content: essay.content, status: essay.status });
      toast.success('Saved');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (id, status) => {
    try { await lawApi.updateEssay(id, { status }); load(); } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await lawApi.deleteEssay(id); if (editing === id) setEditing(null); load(); toast.success('Deleted'); } catch (e) { toast.error(e.message); }
  };

  const wordCount = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length;

  if (loading) return <div className={`h-64 animate-pulse rounded-lg ${dark ? 'bg-[#1C1E20]' : 'bg-[#E8E6E1]'}`} />;

  // If editing, show full editor
  if (editing) {
    const essay = essays.find(e => e.id === editing);
    if (!essay) { setEditing(null); return null; }
    const wc = wordCount(essay.content);
    const stCfg = STATUS_CONFIG[essay.status] || STATUS_CONFIG.not_started;
    return (
      <div className="space-y-4" data-testid="law-essay-editor">
        <div className="flex items-center justify-between">
          <button onClick={() => setEditing(null)} className={`text-xs ${textMuted} hover:${textSecondary}`}>&larr; Back to essays</button>
          <div className="flex items-center gap-2">
            <select className={`h-7 px-2 text-[10px] rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white'}`}
              value={essay.status} onChange={e => { essay.status = e.target.value; handleStatusChange(essay.id, e.target.value); }}>
              {DRAFT_STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>)}
            </select>
            <button onClick={() => handleSave(essay)} disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-xs rounded hover:bg-[#333] disabled:opacity-50">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
          </div>
        </div>
        <div className={`${cardBg} border ${borderColor} rounded-lg p-5`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className={`text-base font-semibold ${textPrimary}`}>{essay.title}</h2>
              {essay.school_name && <p className={`text-[11px] ${textMuted}`}>{essay.school_name}</p>}
            </div>
            <div className={`text-right text-[11px] ${textMuted}`}>
              <span className={wc > (essay.word_limit || 9999) ? 'text-red-500 font-medium' : ''}>{wc}</span>
              {essay.word_limit ? ` / ${essay.word_limit} words` : ' words'}
              <br />v{essay.version || 1}
            </div>
          </div>
          {essay.prompt && <p className={`text-xs ${textSecondary} mb-3 italic`}>{essay.prompt}</p>}
          <textarea className={editorClass}
            value={essay.content || ''}
            onChange={e => { essay.content = e.target.value; setEssays([...essays]); }}
            rows={18} placeholder="Begin writing..." style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }} />
          {essay.reviewer_comments && (
            <div className={`mt-3 p-3 rounded border ${dark ? 'border-[#333] bg-[#1C1E20]' : 'border-[#E8E6E1] bg-[#F7F6F3]'}`}>
              <p className={`text-[10px] font-semibold ${textSecondary} mb-1`}>Advisor Comments</p>
              <p className={`text-xs ${textPrimary}`}>{essay.reviewer_comments}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="law-essays">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-lg font-semibold ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}>Essays & Documents</h1>
          <p className={`text-[11px] ${textMuted} mt-0.5`}>{essays.length} document{essays.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setAdding(!adding)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] text-white text-xs rounded hover:bg-[#333]"
          data-testid="law-add-essay">
          <Plus className="h-3.5 w-3.5" /> New Document
        </button>
      </div>

      {adding && (
        <form onSubmit={handleCreate} className={`${cardBg} border ${borderColor} rounded-lg p-4 space-y-3`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <input className={inputClass} placeholder="Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus data-testid="law-essay-title-input" />
            </div>
            <select className={inputClass} value={form.essay_type} onChange={e => setForm({ ...form, essay_type: e.target.value })}>
              {ESSAY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input className={inputClass} type="number" placeholder="Word limit" value={form.word_limit} onChange={e => setForm({ ...form, word_limit: e.target.value })} />
            <div className="col-span-2">
              <input className={inputClass} placeholder="School name (optional)" value={form.school_name} onChange={e => setForm({ ...form, school_name: e.target.value })} />
            </div>
            <div className="col-span-2">
              <input className={inputClass} placeholder="Prompt (optional)" value={form.prompt} onChange={e => setForm({ ...form, prompt: e.target.value })} />
            </div>
          </div>
          <button type="submit" className="px-4 py-1.5 bg-[#1A1A1A] text-white text-xs rounded hover:bg-[#333]" data-testid="law-essay-submit">Create</button>
        </form>
      )}

      {essays.length === 0 && !adding && (
        <div className={`${cardBg} border ${borderColor} rounded-lg p-8 text-center`}>
          <BookOpen className={`h-8 w-8 ${textMuted} mx-auto mb-2`} strokeWidth={1} />
          <p className={`text-sm ${textSecondary}`}>No essays or documents yet</p>
          <p className={`text-xs ${textMuted} mt-1`}>Create your personal statement to get started</p>
        </div>
      )}

      <div className="space-y-2">
        {essays.map(essay => {
          const stCfg = STATUS_CONFIG[essay.status] || STATUS_CONFIG.not_started;
          const typeLabel = ESSAY_TYPES.find(t => t.value === essay.essay_type)?.label || essay.essay_type;
          const wc = wordCount(essay.content);
          return (
            <div key={essay.id} className={`${cardBg} border ${borderColor} rounded-lg overflow-hidden`} data-testid={`law-essay-${essay.id}`}>
              <button onClick={() => setEditing(essay.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left ${hoverBg} transition-colors`}>
                <div className={`w-2 h-2 rounded-full ${stCfg.color}`} />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium ${textPrimary} block truncate`}>{essay.title}</span>
                  <span className={`text-[10px] ${textMuted}`}>{typeLabel}{essay.school_name ? ` · ${essay.school_name}` : ''}</span>
                </div>
                <span className={`text-[10px] ${textMuted}`}>{wc} words</span>
                <span className={`px-2 py-0.5 text-[9px] rounded ${dark ? 'bg-[#1C1E20]' : 'bg-[#F2F1EE]'} ${textSecondary}`}>{stCfg.label}</span>
                <span className={`text-[10px] ${textMuted}`}>v{essay.version || 1}</span>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(essay.id, essay.title); }}
                  className="text-red-400 hover:text-red-500 p-1"><Trash2 className="h-3 w-3" /></button>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
